import React, { useState, useEffect } from 'react';
import { collection, addDoc, doc, getDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { FaCalendarAlt, FaTimes, FaUserMd, FaUser, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import styles from './AddVisitModal.module.css';
import { normalizePatientId } from '../../utils/patientAccess';

const AddVisitModal = ({ project, onClose, onVisitAdded }) => {
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [visitPeriodBefore, setVisitPeriodBefore] = useState('');
  const [visitPeriodAfter, setVisitPeriodAfter] = useState('');
  const [patientId, setPatientId] = useState('');
  const [visitType, setVisitType] = useState('');
  const [visitNumber, setVisitNumber] = useState('');
  // Proje + hastaya göre hesaplanan sıradaki vizit numarası (öneri)
  const [suggestedVisitNumber, setSuggestedVisitNumber] = useState(null);
  const [visitInfo, setVisitInfo] = useState('');
  const [assignedEmployees, setAssignedEmployees] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [screenFail, setScreenFail] = useState(false);
  const [willRepeat, setWillRepeat] = useState(false);
  const [visitEndDate, setVisitEndDate] = useState('');
  const [visitRepeatPeriod, setVisitRepeatPeriod] = useState('');
  const [visitRepeatUnit, setVisitRepeatUnit] = useState('day');
  const [addToCalendar, setAddToCalendar] = useState(false);
  const [projectPatients, setProjectPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  useEffect(() => {
    const fetchProjectEmployees = async () => {
      try {
        const projectRef = doc(db, 'Projects', project.id);
        const projectDoc = await getDoc(projectRef);
        const projectData = projectDoc.data();

        let employees = [];
        if (projectData && projectData.employees) {
          if (typeof projectData.employees === 'object') {
            employees = Object.values(projectData.employees);
          }
        }
        setAssignedEmployees(employees);
      } catch (error) {
        console.error('Error fetching project employees:', error);
        setAssignedEmployees([]);
      }
    };

    const fetchPatients = async () => {
      setLoadingPatients(true);
      try {
        const patientsQuery = query(
          collection(db, 'Patients'),
          where('projectId', '==', project.id)
        );
        const patientsSnapshot = await getDocs(patientsQuery);
        const patientsData = patientsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProjectPatients(patientsData);
      } catch (error) {
        console.error('Error fetching patients:', error);
        setProjectPatients([]);
      } finally {
        setLoadingPatients(false);
      }
    };

    fetchProjectEmployees();
    fetchPatients();
  }, [project.id]);

  const createGoogleCalendarUrl = (projectName) => {
    try {
      const startDate = new Date(visitPeriodBefore);
      const endDate = new Date(visitPeriodAfter);
      endDate.setDate(endDate.getDate() + 1);

      const formatDateForGCal = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}${month}${day}`;
      };

      const startDateFormatted = formatDateForGCal(startDate);
      const endDateFormatted = formatDateForGCal(endDate);

      const eventTitle = encodeURIComponent(`Ziyaret: ${projectName}`);
      const eventLocation = encodeURIComponent('');
      const eventDetails = encodeURIComponent(
        `Hasta ID: ${patientId || 'Belirtilmemiş'}\n` +
        `${visitInfo || ''}`
      );

      return `https://www.google.com/calendar/render?action=TEMPLATE&text=${eventTitle}&dates=${startDateFormatted}/${endDateFormatted}&details=${eventDetails}&location=${eventLocation}&sf=true&output=xml`;
    } catch (error) {
      console.error('Error creating calendar URL:', error);
      return '';
    }
  };

  /**
   * Kaçıncı vizit olduğunu, aynı hastanın bu projedeki en yüksek vizit
   * numarasının bir fazlası olarak önerir; kaydı yoksa 1. Alan serbesttir,
   * kullanıcı öneriyi değiştirebilir. Hasta değişince yeniden hesaplanır.
   *
   * Sorgu projectId ile yapılır, hasta eşleşmesi istemcide normalize edilerek
   * kontrol edilir; böylece farklı yazılmış hasta kimlikleri öneriyi kaçırmaz.
   */
  useEffect(() => {
    const normalizedPatient = normalizePatientId(patientId);
    if (!project?.id || !normalizedPatient) {
      setSuggestedVisitNumber(null);
      return undefined;
    }

    let cancelled = false;
    const suggestVisitNumber = async () => {
      try {
        const snapshot = await getDocs(
          query(collection(db, 'Visits'), where('projectId', '==', project.id))
        );
        if (cancelled) return;

        const numbers = snapshot.docs
          .map((d) => d.data())
          .filter((v) => normalizePatientId(v.patientId) === normalizedPatient)
          .map((v) => Number(v.visitNumber))
          .filter((n) => Number.isFinite(n) && n > 0);

        const next = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
        setSuggestedVisitNumber(next);
        setVisitNumber(String(next));
      } catch (error) {
        console.error('Vizit numarası önerilemedi:', error);
        if (!cancelled) setSuggestedVisitNumber(null);
      }
    };

    suggestVisitNumber();
    return () => {
      cancelled = true;
    };
  }, [project?.id, patientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (!visitPeriodBefore || !visitPeriodAfter || !visitDate) {
        toast.error('Lütfen tüm tarih alanlarını doldurun!');
        setIsSubmitting(false);
        return;
      }

      const startDate = new Date(visitPeriodBefore);
      const endDate = new Date(visitPeriodAfter);
      const firstVisit = new Date(visitDate);

      const daysBefore = Math.ceil((firstVisit - startDate) / (1000 * 60 * 60 * 24));
      const daysAfter = Math.ceil((endDate - firstVisit) / (1000 * 60 * 60 * 24));

      if (startDate > firstVisit || endDate < firstVisit) {
        toast.error('İlk ziyaret tarihi, başlangıç ve bitiş tarihleri arasında olmalıdır!');
        setIsSubmitting(false);
        return;
      }

      const periodString = daysBefore > 0 || daysAfter > 0 ? `-${daysBefore},+${daysAfter}` : '0';
      const totalDays = daysBefore + daysAfter;

      const employeeObj = assignedEmployees.find(emp => emp.id === selectedEmployee);

      const baseVisitData = {
        projectId: project.id,
        employeeId: selectedEmployee,
        employeeName: employeeObj ? `${employeeObj.name} ${employeeObj.surname}` : '',
        period: periodString,
        patientId: normalizePatientId(patientId),
        visitType: visitType || '',
        visitNumber: visitNumber ? Number(visitNumber) : '',
        info: visitInfo,
        createdAt: new Date(),
        preVisitForm: false,
        screenFail: visitType === 'hastane' && screenFail ? true : false
      };

      const startDateTime = new Date(visitDate);
      const visitPromises = [];

      if (willRepeat) {
        const endDateTime = new Date(visitEndDate);
        const repeatPeriod = Number(visitRepeatPeriod);

        // Periyot 0/geçersizse tarih hiç ilerlemez ve döngü sonsuza girerdi.
        if (!Number.isFinite(repeatPeriod) || repeatPeriod <= 0) {
          toast.error('Tekrar periyodu 0’dan büyük olmalıdır!');
          setIsSubmitting(false);
          return;
        }

        // Tekrar eden ziyaretler aynı ziyaretin devamı değil, sıradaki
        // ziyaretlerdir: takvimde ayırt edilebilmesi için numara her tekrarda
        // bir artar (2. Visit, 3. Visit ...). Numara girilmemişse boş kalır.
        const startNumber = visitNumber ? Number(visitNumber) : null;
        let repeatIndex = 0;

        let currentVisitDate = new Date(startDateTime);
        while (currentVisitDate <= endDateTime) {
          const visitData = {
            ...baseVisitData,
            visitNumber: startNumber !== null ? startNumber + repeatIndex : '',
            firstVisitDate: currentVisitDate.toISOString().split('T')[0],
          };
          visitPromises.push(addDoc(collection(db, 'Visits'), visitData));
          repeatIndex += 1;

          currentVisitDate = new Date(currentVisitDate);
          if (visitRepeatUnit === 'week') {
            currentVisitDate.setDate(currentVisitDate.getDate() + (7 * repeatPeriod));
          } else if (visitRepeatUnit === 'month') {
            currentVisitDate.setMonth(currentVisitDate.getMonth() + repeatPeriod);
          } else {
            currentVisitDate.setDate(currentVisitDate.getDate() + repeatPeriod + totalDays);
          }
        }
      } else {
        const visitData = {
          ...baseVisitData,
          firstVisitDate: startDateTime.toISOString().split('T')[0],
        };
        visitPromises.push(addDoc(collection(db, 'Visits'), visitData));
      }

      await Promise.all(visitPromises);

      // Send email notification to employee
      try {
        const response = await fetch('https://us-central1-habcare-8ecad.cloudfunctions.net/sendEmployeeVisitNotification', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Origin': window.location.origin
          },
          body: JSON.stringify({
            employeeId: selectedEmployee,
            visitDate: visitDate,
            projectName: project.titleShort || 'Not specified',
            period: periodString,
            info: visitInfo || ''
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Email notification failed:', errorData);
        }
      } catch (emailError) {
        console.error('Error sending email notification:', emailError);
      }

      // Google Calendar
      if (addToCalendar) {
        const projectName = project.titleShort || project.title || 'Ziyaret';
        const calendarUrl = createGoogleCalendarUrl(projectName);
        if (calendarUrl) {
          window.open(calendarUrl, '_blank');
        }
      }

      toast.success('Visit(ler) başarıyla eklendi!');
      onVisitAdded();
      onClose();
    } catch (error) {
      console.error('Error adding visit:', error);
      toast.error('Visit eklenirken bir hata oluştu!');
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            Yeni Visit Ekle
          </h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>

        {project.titleShort && (
          <div className={styles.projectBadge}>
            {project.titleShort}
          </div>
        )}

        <div className={styles.modalContent}>
          <form onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                <FaUserMd style={{ marginRight: '8px' }} />
                Çalışan
              </label>
              <select
                className={styles.formSelect}
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                required
              >
                <option value="">Lütfen seçiniz</option>
                {assignedEmployees.map((employee, index) => (
                  <option key={employee.id || index} value={employee.id}>
                    {employee.name} {employee.surname}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                <FaUser style={{ marginRight: '8px' }} />
                Hasta ID
              </label>
              {loadingPatients ? (
                <div className={styles.formInput} style={{ display: 'flex', alignItems: 'center', color: '#718096' }}>
                  Hastalar yükleniyor...
                </div>
              ) : projectPatients.length > 0 ? (
                <select
                  className={styles.formSelect}
                  value={patientId}
                  onChange={(e) => setPatientId(e.target.value)}
                >
                  <option value="">Hasta Seçiniz (Opsiyonel)</option>
                  {projectPatients.map(patient => (
                    <option key={patient.id} value={patient.patientId}>
                      {patient.patientId} {patient.status ? `(${patient.status})` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div className={styles.formInput} style={{ display: 'flex', alignItems: 'center', color: '#718096', fontSize: '0.9rem' }}>
                  Bu projede kayıtlı hasta bulunamadı
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                <FaCalendarAlt style={{ marginRight: '8px' }} />
                İlk Ziyaret Tarihi
              </label>
              <input
                type="date"
                className={styles.formInput}
                value={visitDate}
                onChange={(e) => setVisitDate(e.target.value)}
                required
              />
            </div>

            <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
              <label className={styles.formLabel}>
                Ziyaret Penceresi
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem', display: 'block' }}>
                    Başlangıç Tarihi
                  </label>
                  <input
                    type="date"
                    className={styles.formInput}
                    required
                    value={visitPeriodBefore}
                    onChange={(e) => setVisitPeriodBefore(e.target.value)}
                    max={visitDate || undefined}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem', display: 'block' }}>
                    Bitiş Tarihi
                  </label>
                  <input
                    type="date"
                    className={styles.formInput}
                    required
                    value={visitPeriodAfter}
                    onChange={(e) => setVisitPeriodAfter(e.target.value)}
                    min={visitDate || undefined}
                  />
                </div>
              </div>
              {visitDate && visitPeriodBefore && visitPeriodAfter && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '4px',
                  border: '1px solid #bfdbfe'
                }}>
                  <small style={{ fontSize: '0.75rem', color: '#1e40af', display: 'block', fontWeight: '500' }}>
                    Ziyaret: {new Date(visitDate).toLocaleDateString('tr-TR', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </small>
                  <small style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem', display: 'block' }}>
                    Pencere: {Math.ceil((new Date(visitDate) - new Date(visitPeriodBefore)) / (1000 * 60 * 60 * 24))} gün önce başlar,
                    {' '}{Math.ceil((new Date(visitPeriodAfter) - new Date(visitDate)) / (1000 * 60 * 60 * 24))} gün sonra biter
                    {' '}(Toplam {Math.ceil((new Date(visitPeriodAfter) - new Date(visitPeriodBefore)) / (1000 * 60 * 60 * 24))} gün)
                  </small>
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Ziyaret Tipi
              </label>
              <select
                className={styles.formSelect}
                value={visitType}
                onChange={(e) => setVisitType(e.target.value)}
              >
                <option value="">Lütfen seçiniz</option>
                <option value="hastane">Hastane Vizit</option>
                <option value="home">Home Vizit</option>
                <option value="telefon">Telefon Vizit</option>
                <option value="unplanning">Unplanning Vizit</option>
                <option value="handover">Handover Vizit</option>
              </select>
            </div>

            {visitType === 'hastane' && (
              <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                <label className={styles.screenFailLabel}>
                  <input
                    type="checkbox"
                    checked={screenFail}
                    onChange={(e) => setScreenFail(e.target.checked)}
                    className={styles.screenFailCheckbox}
                  />
                  Screen Fail mi?
                </label>
                {screenFail && (
                  <small className={styles.screenFailNote}>
                    Screen fail olarak işaretlenen kayıtlar ziyaret olarak sayılmaz, yapılan iş olarak kaydedilir.
                  </small>
                )}
              </div>
            )}

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Kaçıncı Visit
              </label>
              <input
                type="number"
                min="1"
                className={styles.formInput}
                value={visitNumber}
                onChange={(e) => setVisitNumber(e.target.value)}
                placeholder="Örn: 1, 2, 3..."
              />
              {suggestedVisitNumber !== null && (
                <small className={styles.fieldHint}>
                  {String(suggestedVisitNumber) === String(visitNumber)
                    ? `Bu hastanın bu projedeki sıradaki viziti: ${suggestedVisitNumber}. İsterseniz değiştirebilirsiniz.`
                    : `Önerilen: ${suggestedVisitNumber}`}
                </small>
              )}
            </div>

            <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
              <label className={styles.formLabel}>
                Bilgi
              </label>
              <textarea
                className={styles.formTextarea}
                value={visitInfo}
                onChange={(e) => setVisitInfo(e.target.value)}
              />
            </div>

            <div className={styles.checkboxGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={willRepeat}
                  onChange={(e) => setWillRepeat(e.target.checked)}
                />
                Tekrar Edecek Mi?
              </label>
            </div>

            {willRepeat && (
              <>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Kapanış Visiti Tarihi</label>
                  <input
                    type="date"
                    className={styles.formInput}
                    required={willRepeat}
                    value={visitEndDate}
                    onChange={(e) => setVisitEndDate(e.target.value)}
                    min={visitDate}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Tekrar Periyodu</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="number"
                      min="1"
                      className={styles.formInput}
                      style={{ flex: 2 }}
                      required={willRepeat}
                      value={visitRepeatPeriod}
                      onChange={(e) => setVisitRepeatPeriod(e.target.value)}
                    />
                    <select
                      value={visitRepeatUnit}
                      onChange={(e) => setVisitRepeatUnit(e.target.value)}
                      className={styles.formSelect}
                      style={{ flex: 1 }}
                      required={willRepeat}
                    >
                      <option value="day">Gün</option>
                      <option value="week">Hafta</option>
                      <option value="month">Ay</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            <div className={styles.checkboxGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={addToCalendar}
                  onChange={(e) => setAddToCalendar(e.target.checked)}
                />
                Bu ziyareti Google Takvimime ekle
              </label>
            </div>

            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
              style={{ width: '100%', marginTop: '0.5rem' }}
            >
              {isSubmitting ? 'İşleniyor...' : 'Kaydet'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddVisitModal;
